import { Select as ChakraSelect, SelectRootProps, createListCollection } from '@chakra-ui/react'
import { type ReactNode } from 'react'
import { Portal, type PortalProps } from './providers'

export interface Props<V extends string, M extends boolean = false>
  extends Omit<
    SelectRootProps,
    'children' | 'onChange' | 'onValueChange' | 'value' | 'collection'
  > {
  _ref?: React.Ref<HTMLDivElement>
  fullWidth?: boolean
  label?: ReactNode
  value?: M extends true ? V[] : V
  multiple?: M
  onChange?: (option: M extends true ? V[] : V) => void
  options?: SelectOption<V>[]
  placeholder?: string
  portal?: boolean | Omit<PortalProps, 'children'>
}

export interface SelectOption<V extends string = string> {
  value: V
  label?: ReactNode
}

export default function Select<V extends string, M extends boolean = false>({
  _ref,
  fullWidth,
  label,
  multiple,
  options = [],
  onChange,
  placeholder = 'Select option',
  portal,
  value,
  ...rest
}: Props<V, M>) {
  const collection = createListCollection({ items: options })

  return (
    <ChakraSelect.Root
      ref={_ref}
      collection={collection}
      //@ts-ignore
      value={multiple ? value : [value]}
      width={fullWidth ? undefined : '250px'}
      multiple={multiple}
      onValueChange={v =>
        //@ts-ignore
        onChange?.(multiple ? v.value : v.value[0])
      }
      {...rest}
    >
      {label && <ChakraSelect.Label>{label}</ChakraSelect.Label>}
      <ChakraSelect.Trigger>
        <ChakraSelect.ValueText placeholder={placeholder} />
        <ChakraSelect.Indicator />
      </ChakraSelect.Trigger>
      <Portal disabled={!portal} {...(typeof portal === 'object' ? portal : {})}>
        <ChakraSelect.Positioner>
          <ChakraSelect.Content>
            {options.map(option => (
              <ChakraSelect.Item item={option} key={option.value}>
                <ChakraSelect.ItemIndicator />
                {option.label}
              </ChakraSelect.Item>
            ))}
          </ChakraSelect.Content>
        </ChakraSelect.Positioner>
      </Portal>
    </ChakraSelect.Root>
  )
}
